import React, { lazy, Suspense, useEffect } from "react";
import { Box, CircularProgress, Typography, Divider, Container } from "@mui/material";
import useSEO from "../../hooks/useSEO";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaidIcon from '@mui/icons-material/Paid';
import SecurityIcon from '@mui/icons-material/Security';
import HeaderLayout from "../../components/HeaderLayout/HeaderLayout";
import backdrop from "../../components/HeadingPage/assets/HeaderbgImage.svg";
import demoImage from "../../components/HeadingPage/assets/DemoImage.svg";
import financeBgImage from "../HomePage/assets/UseCasesBgImage.svg";
import invoiceProcessingImage from "../HomePage/assets/EmployeesImage.svg";
import fraudDetectionImage from "../HomePage/assets/EmployeesImage.svg";
import complianceImage from "../HomePage/assets/EmployeesImage.svg";
import image1 from "./assets/image1.svg";
import image2 from "./assets/image2.svg";
import image3 from "./assets/image3.svg";
import image4 from "./assets/image4.svg";
const FeaturesPageLayout = lazy(() => import("../../components/FeaturesPageLayout/FeaturesPageLayout"));
const UseCasesSection = lazy(() => import("../../components/UseCases/UseCase"));
const Testimonials = lazy(() => import("../../components/CompliantFold/Testimonials"));
const CustomUsecaseForm = lazy(() => import("../../components/CustomUsecaseForm/CustomUsecaseForm"));

// Enhanced loading fallback with better visual appearance
const LoadingFallback = () => (
    <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        py: { xs: 6, sm: 8, md: 10 },
        minHeight: "300px"
    }}>
        <CircularProgress size={40} thickness={4} />
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontWeight: 500 }}
        >
            Loading content...
        </Typography>
    </Box>
);

// Enhanced lazy loading component with better sizing
const LazyComponent = ({ children, threshold = 0.1 }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold, rootMargin: "200px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [threshold]);

    return (
        <div ref={ref}>
            {isVisible ? children : (
                <Box sx={{
                    height: { xs: "150px", sm: "180px", md: "200px" },
                    width: "100%",
                    transition: "height 0.3s ease"
                }} />
            )}
        </div>
    );
};

export default function Finance() {
    useSEO({
        title: "Finance Solutions - Beanbag AI",
        description: "Automate your finance workflows with Beanbag AI. Our platform streamlines reconciliation, accounts payable/receivable, and fraud detection with intelligent AI.",
        canonical: "https://www.beanbag.ai/finance",
        keywords: "finance automation, accounting AI, fraud detection, accounts payable, accounts receivable",

        ogTitle: "Finance Automation Solutions | Beanbag AI",
        ogDescription: "Empower your finance team with intelligent automation for reconciliation, AP/AR, and fraud detection",
        ogImage: "https://www.beanbag.ai/images/finance-solution-card.jpg",
        ogUrl: "https://www.beanbag.ai/finance",
        ogType: "website",

        twitterCard: "summary_large_image",
        twitterSite: "@BeanbagAI",

        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Finance Automation Solutions",
            "url": "https://www.beanbag.ai/finance",
            "description": "Beanbag AI empowers finance teams to streamline operations, mitigate risks, and drive smarter decisions through Agentic automations.",
            "isPartOf": {
                "@type": "WebSite",
                "name": "Beanbag AI",
                "url": "https://www.beanbag.ai"
            }
        }
    });

    useEffect(() => {
        // Preload critical components
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => {
                import("../../components/KeyFeatures/KeyFeatures");
                import("../../components/UseCases/UseCase");
            });
        }

        // Load non-critical components when tab is visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                import("../../components/CompliantFold/Compliant");
                import("../../components/IntegrationSupport/IntegrationSupport");
                import("../../components/CustomUsecaseForm/CustomUsecaseForm");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const financeFeatures = [
        {
            id: 1,
            title: "Account Reconciliation",
            icon: <AccountBalanceIcon fontSize="large" />,
            image: image1,
            description: "Beanbag AI's reconciliation model automates transaction matching, identifies discrepancies, and ensures compliance. Say goodbye to spreadsheets and hello to intelligent automation.",
            benefits: [
                { primary: "70% Fewer Errors", secondary: "AI-powered matching reduces manual reconciliation mistakes." },
                { primary: "Real-Time Visibility", secondary: "Dashboards provide instant insights into financial data." },
                { primary: "Audit-Ready", secondary: "Comprehensive audit trails ensure compliance with regulatory standards." }
            ],
            howItWorks: [
                { primary: "Data Integration", secondary: "Seamlessly connect bank statements, invoices, and subledger entries." },
                { primary: "AI Matching", secondary: "Transactions are automatically matched across data sources." },
                { primary: "Exception Handling", secondary: "Flag discrepancies and resolve them with automated suggestions." }
            ]
        },
        {
            id: 2,
            title: "Accounts Payable",
            icon: <ReceiptIcon fontSize="large" />,
            image: image2,
            description: "From invoice capture to payment processing, you can do end-to-end automation with Beanbag AI.",
            benefits: [
                { primary: "90% Faster Processing", secondary: "AI extracts and validates invoice data in seconds." },
                { primary: "Zero Duplicates", secondary: "Advanced anomaly detection prevents overpayments." },
                { primary: "Optimized Cash Flow", secondary: "Prioritize payments to maximize savings and avoid late fees." }
            ],
            howItWorks: [
                { primary: "Invoice Capture", secondary: "AI reads invoices in any format (PDF, email, paper scans)." },
                { primary: "Validation", secondary: "Cross-checks invoice data against purchase orders and contracts." },
                { primary: "Approval Workflow", secondary: "Routes invoices to the right approvers with intelligent routing." },
                { primary: "Payment Processing", secondary: "Automates payments and integrates with your payment gateways." }
            ]
        },
        {
            id: 3,
            title: "Accounts Receivables",
            icon: <PaidIcon fontSize="large" />,
            image: image3,
            description: "Streamline receivables management with automated invoicing, reminders, and predictive analytics for improved customer experience.",
            benefits: [
                { primary: "Faster Payments", secondary: "AI sends intelligent reminders to reduce overdue invoices." },
                { primary: "Predictive Insights", secondary: "Forecast cash flow and identify at-risk accounts." },
                { primary: "Improved Customer Experience", secondary: "Personalized communication keeps clients engaged." }
            ],
            howItWorks: [
                { primary: "Invoice Generation", secondary: "Create and send invoices automatically." },
                { primary: "Payment Tracking", secondary: "Monitor payment status in real time." },
                { primary: "Delinquency Management", secondary: "AI predicts and addresses payment risks proactively." }
            ]
        },
        {
            id: 4,
            title: "Fraud Detection and Prevention",
            icon: <SecurityIcon fontSize="large" />,
            image: image4,
            description: "Autonomous agents continuously monitor transaction patterns to flag anomalies and prevent fraudulent activities. Protect your bottom line with AI.",
            benefits: [
                { primary: "Real-Time Alerts", secondary: "Immediate notifications for suspicious transactions." },
                { primary: "Zero False Positives", secondary: "Advanced machine learning minimizes errors." },
                { primary: "Forensic Analysis", secondary: "Trace fraud origins for improved prevention strategies." }
            ],
            howItWorks: [
                { primary: "NLP", secondary: "Scans text-based communications like emails and chat messages to identify phishing attempts and fraudulent claims." },
                { primary: "Behavioral Biometrics", secondary: "Analyzes user behavior such as typing speed, mouse movements, and touchscreen interactions." }
            ]
        }
    ];

    const financeCasesData = [
        {
            id: 1,
            image: invoiceProcessingImage,
            title: "End-to-End Automation",
            description:
                "From risk assessment to compliance reporting, Beanbag AI handles every step of your financial workflow.",
        },
        {
            id: 2,
            image: fraudDetectionImage,
            title: "Seamless Integrations",
            description:
                "Connect with your existing tools (ERP, CRM, accounting software) via pre-built connectors",
        },
        {
            id: 3,
            image: complianceImage,
            title: "Proven Results",
            description:
                "Join finance leaders who've cut processing times by 80%, reduced fraud losses by 95%, and achieved 100% compliance accuracy. ",
        },
    ];

    return (
        <Box sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden"
        }}>
            {/* Hero Section with HeaderLayout */}
            <Box sx={{
                mb: { xs: 4, sm: 6, md: 8, lg: 10 },
                px: { xs: 0, sm: 2, md: 3 }
            }}>
                <HeaderLayout
                    backgroundImage={backdrop}
                    title="Automate, Analyze, and Act with Confidence"
                    subtitle="Beanbag AI empowers finance teams to streamline operations, mitigate risks, and drive smarter decisions through Agentic automations."
                    imageUrl={demoImage}
                    imageAlt="Platform Demo"
                    ctaButtonText="Get Started"
                    ctaButtonLink="https://flow.beanbag.ai/signup"
                    secondaryButtonText="Request a Demo"
                    showSecondaryButton={true}
                />
            </Box>

            {/* Features Section */}
            <Box sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2, sm: 3, md: 0 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <FeaturesPageLayout
                            title={
                                <>
                                    Why Automate Finance with <br />
                                    Beanbag AI?
                                </>
                            }
                            description="Finance teams face mounting pressure to reduce manual work, ensure compliance, and deliver actionable insights. Beanbag AI combines cutting-edge AI with intuitive workflows to tackle your biggest challenges"
                            sectionTitle="How It Works"
                            features={financeFeatures}
                            backgroundColor="#121212"
                            containerProps={{
                                py: { xs: 0, sm: 8, md: 10, lg: 12 },
                                px: { xs: 2, sm: 3, md: 4 }
                            }}
                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Use Cases Section */}
            <Box sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasesSection
                            title="Why Choose Beanbag AI"
                            backgroundImage={financeBgImage}
                            cardsData={financeCasesData}
                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Testimonials Section with Improved Container */}
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: {
                        xs: "100%",
                        sm: "95%",
                        md: "90%",
                        lg: "1200px"
                    },
                    px: { xs: 2, sm: 3, md: 4 }, // Add horizontal padding
                    py: { xs: 4, sm: 6, md: 8 }, // Add vertical padding
                    mx: "auto", // Center the container
                }}>
                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        my: { xs: 4, sm: 5, md: 6, lg: 7 },
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        fontSize: { xs: "24px", sm: "34px", md: "42px", lg: "48px" },
                        color: "white",
                        fontWeight: 600,
                        textAlign: "center",
                        textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                        animation: "fadeIn 1.2s ease-in-out 0.3s both",
                        mb: { xs: 3, sm: 4, md: 5, lg: 6 },
                        px: { xs: 1, sm: 2, md: 0 }
                    }}
                >
                    Trusted by Finance Teams Worldwide
                </Typography>

                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <Box sx={{
                            px: { xs: 0, sm: 1, md: 2 },
                            py: { xs: 2, sm: 3, md: 4 }
                        }}>
                            <Testimonials />
                        </Box>
                    </Suspense>
                </LazyComponent>

                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        mt: { xs: 4, sm: 5, md: 6 },
                        mb: { xs: 6, sm: 8, md: 10 }
                    }}
                />
            </Container>

            {/* Contact Form Section */}
            <Box sx={{
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <CustomUsecaseForm />
                    </Suspense>
                </LazyComponent>
            </Box>
        </Box>
    );
}